/**
 * Slider - Lightweight carousel/slider/gallery utility
 *
 * One class, a handful of ways to move. The options pick a pointer mode (what
 * a mouse drag or a finger does to the track), and from that a step mode (how
 * the arrows and arrow keys move it) and a settle mode (where a free scroll
 * comes to rest). Each mode is its own method; `scroll()` and
 * `settleOnNearestItem()` only dispatch on the resolved mode.
 *
 * Modes, the options that select them, and the markup each one needs:
 *
 * Free scroll, settle on a slide (the default: snap: false, snapOnRelease: true)
 *   Mouse: the track follows the pointer 1:1. Touch: native momentum. Once
 *   the drag or fling ends, the nearest slide's start lines up with the
 *   track's start (settleOnNearestItem). Arrows and keys: one slide,
 *   start-aligned (scrollToAdjacentItem). No CSS scroll-snap on the scroll
 *   target: mandatory snap fights the drag and stops a fling at every slide.
 *
 * Free scroll, no settle (snapOnRelease: false)
 *   As above, but the track stays wherever the drag or fling leaves it, and
 *   arrows and keys move a fraction of the track width (scrollByFraction).
 *
 * Slide (snap: true)
 *   Mouse: a swipe moves one slide. Touch: the browser snaps natively, so the
 *   scroll target needs `snap-x snap-mandatory` (or an inline scroll-snap-type)
 *   and each item `snap-start`; without them a finger free-scrolls.
 *   Arrows and keys: one slide, start-aligned. Autoplay is for this mode.
 *
 * Free scroll, settle centered (snapAlign: 'center')
 *   As the default, but the nearest slide's center lines up with the track's center
 *   (snapNearestToCenter), and arrows and keys step from the centered slide to
 *   the next one centered (scrollToCenteredItem). The list needs side padding
 *   of (track width - slide width) / 2 so the first and last slides can reach
 *   the center. snapAlign does nothing without snapOnRelease.
 *
 * Page step (pageStep: true)
 *   Free scroll, but arrows and keys move a page of slides (scrollByPage).
 *   Pass snapOnRelease: false too unless the track should also settle.
 *   Needs one row of equal-width items. pageStep takes precedence over the
 *   other step modes.
 *
 * Data attributes:
 * - [data-slider-scroll-target] - Scrollable container (required)
 * - [data-slider-item] - Individual slide/item elements (required)
 * - [data-slider-prev] - Previous button (optional)
 * - [data-slider-next] - Next button (optional)
 * - [data-slider-no-drag] - A mouse or touch start inside this element is left
 *   alone, e.g. a video's own controls (optional)
 * - [data-slider-manual] - On the root: theme.js's auto-init skips it; the
 *   section's own script builds the Slider with the options it needs
 * - data-autoplay / data-interval - On the root: read by theme.js's auto-init
 *
 * Keyboard (Arrow Left/Right) and prev/next buttons work in every mode.
 * wrap: true applies to the slide step modes.
 *
 * @class
 * @example
 * const slider = new Slider(element); // free scroll, settles left
 * const banner = new Slider(element, { snap: true, autoplay: true });
 * slider.onScroll((index) => console.log('Current:', index));
 * slider.destroy();
 */
/** Slack, in px, for fractional scroll positions when checking whether a track is at its end. */
const SCROLL_END_TOLERANCE = 2;

/** Slide mode: a mouse move shorter than this is a click, not a swipe. */
const SWIPE_THRESHOLD_PX = 30;

/** Free scroll: a mouse move shorter than this is a click, not a drag. */
const DRAG_THRESHOLD_PX = 5;

/** Free scroll: clicks are swallowed for this long after a drag so the release can't activate a slide. */
const CLICK_BLOCK_AFTER_DRAG_MS = 300;

/** Free scroll on touch: the fling is over once no scroll event has fired for this long. */
const TOUCH_SETTLE_DELAY_MS = 80;

/** onScroll(): the callback fires once scrolling has paused for this long. */
const SCROLL_END_DEBOUNCE_MS = 100;

/** Free scroll: how far one arrow press moves, as a fraction of the track width. */
const FREE_SCROLL_STEP_FRACTION = 0.25;

/** Autoplay interval when the caller gives none. */
const DEFAULT_AUTOPLAY_INTERVAL_MS = 5000;

/** snapAlign values: line the slide's start, or its center, up with the track. */
const SNAP_ALIGN_START = 'start';
const SNAP_ALIGN_CENTER = 'center';

/** Step modes: what one arrow press or arrow key moves. Resolved once from the options. */
const STEP_PAGE = 'page';
const STEP_ITEM = 'item';
const STEP_ITEM_CENTERED = 'item-centered';
const STEP_FRACTION = 'fraction';

/** Settle modes: where a free-scroll drag or fling comes to rest. Resolved once from the options. */
const SETTLE_NONE = 'none';
const SETTLE_ITEM = 'item';
const SETTLE_ITEM_CENTERED = 'item-centered';

/** Arrow keys the slider handles, and the direction each moves. */
const ARROW_KEY_DELTA = { ArrowLeft: -1, ArrowRight: 1 };

/**
 * Elements a Tab press would land on inside a slide. tabindex="-1" is excluded
 * everywhere: product cards often carry a duplicate image link taken out of the
 * tab order, and focusing that would skip the real one.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
].map((selector) => `${selector}:not([tabindex="-1"])`).join(', ');

/**
 * Which step mode the options add up to. See the mode list at the top.
 *
 * @param {Object} options - Resolved Slider options
 * @returns {string} One of the STEP_* values
 */
function resolveStepMode({ snap, snapOnRelease, snapAlign, pageStep }) {
  if (pageStep) return STEP_PAGE;
  if (snapOnRelease && snapAlign === SNAP_ALIGN_CENTER) return STEP_ITEM_CENTERED;
  if (snap || snapOnRelease) return STEP_ITEM;
  return STEP_FRACTION;
}

/**
 * Which settle mode the options add up to. Slide mode leaves settling to CSS
 * scroll-snap, so only free scroll with snapOnRelease settles here.
 *
 * @param {Object} options - Resolved Slider options
 * @returns {string} One of the SETTLE_* values
 */
function resolveSettleMode({ snap, snapOnRelease, snapAlign }) {
  if (snap || !snapOnRelease) return SETTLE_NONE;
  return snapAlign === SNAP_ALIGN_CENTER ? SETTLE_ITEM_CENTERED : SETTLE_ITEM;
}

export default class Slider {
  /**
   * Skip swipe/drag handling for nested interactive content.
   *
   * @param {EventTarget|null} target
   * @returns {boolean}
   * @private
   */
  shouldIgnorePointerStart(target) {
    return target instanceof Element && Boolean(target.closest('[data-slider-no-drag]'));
  }

  /**
   * Initialize a new slider.
   *
   * @param {HTMLElement} root - Slider root element (parent of all data attributes)
   * @param {Object} options - Configuration options
   * @param {boolean} options.snap - Slide mode: swipe and step one slide at a
   *   time, native CSS snap on touch (default: false, free scroll).
   * @param {boolean} options.snapOnRelease - Free scroll only: settle on the
   *   nearest slide once a drag or touch fling ends (default: true).
   * @param {'start'|'center'} options.snapAlign - Which part of the slide
   *   snapOnRelease lines up with the track: its start, or its center with the
   *   track's center. Arrows step the same way (default: 'start').
   * @param {boolean} options.pageStep - Arrows and arrow keys move a page at a
   *   time, less one item: the last item in view becomes the first (or the
   *   first the last, going back), so the shopper keeps their place. Assumes
   *   equal-width items in one row (default: false)
   * @param {boolean} options.wrap - Wrap to opposite end when advancing past edges
   * @param {boolean} options.autoplay - Enable automatic sliding (default: false)
   * @param {number} options.interval - Autoplay interval in milliseconds (default: 5000)
   * @param {boolean} options.rootTabStop - Make the root focusable so arrow-key
   *   nav works when slides hold no focusable elements (default: true). Pass
   *   false when every slide is keyboard-reachable on its own (e.g. product
   *   cards) — the keydown handler still fires via bubbling from the focused
   *   child, and the root would only add an empty tab stop.
   * @param {boolean} options.focusFollowsArrows - When focus is inside a slide,
   *   arrow keys move focus to the adjacent slide (which the browser scrolls
   *   into view) instead of scrolling the track out from under the focused
   *   element (default: false).
   */
  constructor(root, options = {}) {
    this.root = root;
    this.container = root.querySelector('[data-slider-scroll-target]');
    this.items = root.querySelectorAll('[data-slider-item]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');

    // Default: free scroll that settles a slide's start at the track's start.
    this.options = {
      snap: false,
      snapOnRelease: true,
      snapAlign: SNAP_ALIGN_START,
      pageStep: false,
      wrap: false,
      autoplay: false,
      interval: DEFAULT_AUTOPLAY_INTERVAL_MS,
      rootTabStop: true,
      focusFollowsArrows: false,
      ...options
    };

    // Resolved once so no method has to re-derive the option combinations.
    this.stepMode = resolveStepMode(this.options);
    this.settleMode = resolveSettleMode(this.options);

    this.autoplayTimer = null;

    if (this.container) this.init();
  }

  /**
   * Initialize slider: bind event listeners, setup controls, start autoplay if configured.
   *
   * @returns {void}
   * @private
   */
  init() {
    // One controller for every persistent listener this init cycle binds (on
    // the root, container, and prev/next buttons — all of which survive a
    // rebuild). destroy() aborts it so a destroy→re-init (variant/combined-
    // listing morph, refreshSettings) can't stack duplicate handlers that make
    // arrows and keyboard nav scroll multiple steps per press.
    this._abort = new AbortController();
    const { signal } = this._abort;

    // Controls. Any interaction restarts a running autoplay timer.
    this.prevBtn?.addEventListener('click', () => {
      this.scroll(-1);
      this.resetAutoplay();
    }, { signal });
    this.nextBtn?.addEventListener('click', () => {
      this.scroll(1);
      this.resetAutoplay();
    }, { signal });

    // Keyboard
    if (this.options.rootTabStop) this.root.tabIndex = 0;
    this.root.addEventListener('keydown', (e) => {
      const delta = ARROW_KEY_DELTA[e.key];
      if (!delta) return;

      if (!this.focusAdjacentItem(e.target, delta)) this.scroll(delta);
      this.resetAutoplay();
      // stopPropagation so a slider nested inside another slider's root (e.g.
      // the UGC gallery inside the product gallery) consumes its own arrow keys
      // instead of letting them bubble up and scroll the outer track too.
      e.preventDefault();
      e.stopPropagation();
    }, { signal });

    // Pointer: a swipe gesture in slide mode, a 1:1 drag in free scroll.
    if (this.options.snap) {
      this.bindSwipe();
    } else {
      this.bindDrag();
    }

    this.container.classList.add('scrollbar-hide');

    this.updateControls();
    this._scrollHandler = () => this.updateControls();
    this.container.addEventListener('scroll', this._scrollHandler, { passive: true, signal });
    this._resizeHandler = () => this.updateControls();
    window.addEventListener('resize', this._resizeHandler, { signal });

    if (this.options.autoplay && this.items.length > 1) {
      this.startAutoplay();
    }
  }

  // ---------------------------------------------------------------------------
  // Pointer modes
  // ---------------------------------------------------------------------------

  /**
   * Slide mode, mouse: a horizontal swipe past SWIPE_THRESHOLD_PX moves one
   * slide. Touch is left to the browser's CSS scroll-snap.
   *
   * @returns {void}
   * @private
   */
  bindSwipe() {
    let startX = 0;
    let dragging = false;

    const { signal } = this._abort;

    this.container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (this.shouldIgnorePointerStart(e.target)) return;
      dragging = true;
      startX = e.screenX;
      this.container.style.cursor = 'grabbing';
      e.preventDefault();
    }, { signal });

    this.container.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    this._swipeMouseUpHandler = (e) => {
      if (!dragging) return;
      dragging = false;
      this.container.style.cursor = '';
      const diff = startX - e.screenX;
      if (Math.abs(diff) > SWIPE_THRESHOLD_PX) this.scroll(diff > 0 ? 1 : -1);
    };
    document.addEventListener('mouseup', this._swipeMouseUpHandler, { signal });
  }

  /**
   * Free scroll, mouse and touch. The mouse drags the track 1:1; a finger
   * scrolls natively with momentum. When the gesture ends, settleOnNearestItem()
   * applies the settle mode (nothing without snapOnRelease).
   *
   * Mouse move/up listen on the document: the prev/next arrows sit above the
   * track (z-index), so listening on the container only would miss moves over
   * them and end the drag early — felt worst when dragging back toward the
   * start, where the pointer crosses the prev arrow.
   *
   * @returns {void}
   * @private
   */
  bindDrag() {
    const { signal } = this._abort;

    this._dragDocMove = (e) => {
      if (!this._dragActive) return;
      const walk = e.clientX - this._dragStartX;
      if (Math.abs(walk) > DRAG_THRESHOLD_PX) this._dragMoved = true;
      this.container.scrollLeft = this._dragScrollStart - walk;
    };

    this._dragDocEnd = () => {
      if (!this._dragActive) return;
      this._dragActive = false;
      document.removeEventListener('mousemove', this._dragDocMove);
      document.removeEventListener('mouseup', this._dragDocEnd);

      this.container.style.cursor = '';
      this.container.style.userSelect = '';
      this.container.style.scrollBehavior = this._dragPrevScrollBehavior ?? '';
      this._dragPrevScrollBehavior = undefined;

      if (!this._dragMoved) return;
      this.settleOnNearestItem();
      this.blockNextClick();
    };

    this.container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (this.shouldIgnorePointerStart(e.target)) return;
      this._dragActive = true;
      this._dragMoved = false;
      this._dragStartX = e.clientX;
      this._dragScrollStart = this.container.scrollLeft;
      this._dragPrevScrollBehavior = this.container.style.scrollBehavior;
      this.container.style.scrollBehavior = 'auto';
      this.container.style.cursor = 'grabbing';
      this.container.style.userSelect = 'none';
      e.preventDefault();
      document.addEventListener('mousemove', this._dragDocMove);
      document.addEventListener('mouseup', this._dragDocEnd);
    }, { signal });

    // Touch: the browser owns the scroll; settle once the fling has stopped.
    this._touchMoveTrack = () => { this._touchMoved = true; };
    this._touchEnd = () => {
      this.container.removeEventListener('touchend', this._touchEnd);
      this.container.removeEventListener('touchcancel', this._touchEnd);
      this.container.removeEventListener('touchmove', this._touchMoveTrack);
      if (this._touchMoved) this.settleAfterFling();
    };
    this.container.addEventListener('touchstart', (e) => {
      if (this.shouldIgnorePointerStart(e.target)) return;
      this._touchMoved = false;
      this.container.addEventListener('touchmove', this._touchMoveTrack, { passive: true, once: true });
      this.container.addEventListener('touchend', this._touchEnd, { once: true });
      this.container.addEventListener('touchcancel', this._touchEnd, { once: true });
    }, { passive: true, signal });

    this.container.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
  }

  /**
   * Free scroll on touch: momentum keeps the track moving after the finger
   * lifts, so wait until scroll events stop before settling. A very short
   * flick may fire no scroll event at all, so the timer also runs on its own.
   *
   * @returns {void}
   * @private
   */
  settleAfterFling() {
    if (this.settleMode === SETTLE_NONE) return;

    // A new fling replaces any settle still waiting from the last one.
    this.cancelPendingSettle();

    const onScroll = () => {
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(settle, TOUCH_SETTLE_DELAY_MS);
    };
    const settle = () => {
      this.cancelPendingSettle();
      this.settleOnNearestItem();
    };
    this._settleScrollHandler = onScroll;
    this.container.addEventListener('scroll', onScroll, { passive: true });
    this._settleTimer = setTimeout(settle, TOUCH_SETTLE_DELAY_MS);
  }

  /**
   * Drop a touch settle that hasn't fired yet: its timer and its scroll listener.
   *
   * @returns {void}
   * @private
   */
  cancelPendingSettle() {
    clearTimeout(this._settleTimer);
    this._settleTimer = null;
    if (this._settleScrollHandler) {
      this.container?.removeEventListener('scroll', this._settleScrollHandler);
      this._settleScrollHandler = null;
    }
  }

  /**
   * After a mouse drag, swallow the click the mouse-up would otherwise fire on
   * whatever slide is under the pointer.
   *
   * @returns {void}
   * @private
   */
  blockNextClick() {
    const block = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    this.container.addEventListener('click', block, { capture: true, once: true });
    setTimeout(() => this.container.removeEventListener('click', block, true), CLICK_BLOCK_AFTER_DRAG_MS);
  }

  // ---------------------------------------------------------------------------
  // Settle modes (free scroll only)
  // ---------------------------------------------------------------------------

  /**
   * Bring the track to rest on the nearest slide, the way the settle mode
   * says. Nothing happens without snapOnRelease.
   *
   * @returns {void}
   * @private
   */
  settleOnNearestItem() {
    switch (this.settleMode) {
      case SETTLE_ITEM:
        this.scrollToItemStart(this.getCurrentIndex());
        break;
      case SETTLE_ITEM_CENTERED:
        this.snapNearestToCenter();
        break;
      default:
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Step modes (arrows, arrow keys, swipes)
  // ---------------------------------------------------------------------------

  /**
   * Move the track `delta` steps. What a step is depends on the step mode: a
   * page, one slide (start- or center-aligned) or a fraction of the track.
   * Arrows, arrow keys and slide-mode swipes all come through here.
   *
   * @param {number} delta - Steps to move (positive = next, negative = prev)
   * @returns {void}
   */
  scroll(delta) {
    switch (this.stepMode) {
      case STEP_PAGE:
        this.scrollByPage(delta);
        break;
      case STEP_ITEM_CENTERED:
        this.scrollToCenteredItem(delta);
        break;
      case STEP_ITEM:
        this.scrollToAdjacentItem(delta);
        break;
      default:
        this.scrollByFraction(delta);
    }
  }

  /**
   * Line a slide's start up with the track's start.
   *
   * @param {number} index - Slide index; out of range does nothing
   * @returns {void}
   * @private
   */
  scrollToItemStart(index) {
    const item = this.items[index];
    if (item) this.container.scrollTo({ left: this.itemStart(item), behavior: 'smooth' });
  }

  /**
   * The scrollLeft at which `item`'s start sits at the track's start, less the
   * track's scroll-padding (the inset a track like Collection List keeps
   * before its first card).
   *
   * Measured from on-screen boxes. offsetLeft only works when the slider root
   * is the items' offset parent and sits flush with the track; a root without
   * `position: relative` (the header banner) would add the page margin and
   * overshoot.
   *
   * @param {Element} item
   * @param {{ left: number, scrollPadding: number }} [origin] - From trackOrigin(); pass one in when measuring many items
   * @returns {number}
   * @private
   */
  itemStart(item, origin = this.trackOrigin()) {
    const offset = item.getBoundingClientRect().left - origin.left;
    return this.container.scrollLeft + offset - origin.scrollPadding;
  }

  /**
   * The track's on-screen left edge and its scroll-padding, read once so a
   * loop over every item doesn't re-read the track's layout and style.
   *
   * @returns {{ left: number, scrollPadding: number }}
   * @private
   */
  trackOrigin() {
    return {
      left: this.container.getBoundingClientRect().left,
      scrollPadding: parseFloat(getComputedStyle(this.container).scrollPaddingLeft) || 0
    };
  }

  /**
   * Slide step: the slide `delta` away from the current one, start-aligned.
   *
   * @param {number} delta - Slides to move (positive = next, negative = prev)
   * @returns {void}
   * @private
   */
  scrollToAdjacentItem(delta) {
    const count = this.items.length;
    if (!count) return;

    const idx = this.getCurrentIndex();
    let newIdx = this.options.wrap && count > 1
      ? (idx + delta + count) % count
      : Math.max(0, Math.min(count - 1, idx + delta));

    // At the end of a track that shows several items, the last few starts
    // all sit past the maximum scroll, so scrolling to one clamps right back
    // to the end and prev would never move. Step back to the first start the
    // track can actually reach. Skipped on a wrap to the end, which should land there.
    if (newIdx < idx) {
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      const origin = this.trackOrigin();
      while (newIdx > 0 && this.itemStart(this.items[newIdx], origin) >= maxScroll - SCROLL_END_TOLERANCE) newIdx--;
    }

    this.scrollToItemStart(newIdx);
  }

  /**
   * Centered step: from the slide nearest the center to the one `delta` away,
   * centered. The start-aligned step would leave it at the left edge and
   * miscount which slide is current.
   *
   * @param {number} delta - Slides to move (positive = next, negative = prev)
   * @returns {void}
   * @private
   */
  scrollToCenteredItem(delta) {
    const offsets = this.centerOffsets();
    const current = this.nearestToCenter(offsets);
    if (current === -1) return;

    const count = offsets.length;
    const target = this.options.wrap && count > 1
      ? (current + delta + count) % count
      : Math.max(0, Math.min(count - 1, current + delta));
    this.container.scrollBy({ left: offsets[target], behavior: 'smooth' });
  }

  /**
   * Scroll so the slide nearest the track's center sits at the center.
   *
   * @returns {void}
   * @private
   */
  snapNearestToCenter() {
    const offsets = this.centerOffsets();
    const nearest = this.nearestToCenter(offsets);
    if (nearest !== -1) this.container.scrollBy({ left: offsets[nearest], behavior: 'smooth' });
  }

  /**
   * How far each slide's center sits from the track's center, in px (negative
   * = left of center). Measured from on-screen boxes rather than offsetLeft,
   * which is relative to the offset parent and not necessarily the track.
   *
   * @returns {number[]}
   * @private
   */
  centerOffsets() {
    const track = this.container.getBoundingClientRect();
    const trackCenter = track.left + track.width / 2;
    return [...this.items].map((item) => {
      const box = item.getBoundingClientRect();
      return box.left + box.width / 2 - trackCenter;
    });
  }

  /**
   * @param {number[]} offsets - From centerOffsets().
   * @returns {number} Index of the slide closest to center, or -1 with no slides.
   * @private
   */
  nearestToCenter(offsets) {
    let nearest = -1;
    offsets.forEach((offset, index) => {
      if (nearest === -1 || Math.abs(offset) < Math.abs(offsets[nearest])) nearest = index;
    });
    return nearest;
  }

  /**
   * Page step: as many items as fully fit, less one, so the item at the far
   * edge in the direction of travel ends up at the near edge.
   *
   * Works in item steps (the distance from one item's start to the next's,
   * which a hover transform doesn't change): at scrollLeft = n steps, item n
   * sits where item 0 sits at rest. The browser clamps a target past either end.
   *
   * @param {number} delta - Pages to move (positive = next, negative = prev)
   * @returns {void}
   * @private
   */
  scrollByPage(delta) {
    if (this.items.length < 2) return;

    const [first, second] = this.items;
    const itemStep = second.offsetLeft - first.offsetLeft;
    if (itemStep <= 0) return;
    const style = getComputedStyle(this.container);
    const viewWidth = this.container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const gap = itemStep - first.offsetWidth;
    const itemsInView = Math.max(1, Math.floor((viewWidth + gap) / itemStep));
    const itemsPerPage = Math.max(1, itemsInView - 1);
    const currentItem = Math.round(this.container.scrollLeft / itemStep);
    this.container.scrollTo({ left: (currentItem + delta * itemsPerPage) * itemStep, behavior: 'smooth' });
  }

  /**
   * Free scroll step: a fraction of the track width.
   *
   * @param {number} delta - Steps to move (positive = next, negative = prev)
   * @returns {void}
   * @private
   */
  scrollByFraction(delta) {
    this.container.scrollBy({ left: delta * this.container.clientWidth * FREE_SCROLL_STEP_FRACTION, behavior: 'smooth' });
  }

  // ---------------------------------------------------------------------------
  // Position
  // ---------------------------------------------------------------------------

  /**
   * With `focusFollowsArrows`, move focus from the slide holding `target` to
   * the first tabbable element of the adjacent slide. Focusing it scrolls the
   * track as far as needed (honouring scroll-padding), so the focused element
   * never ends up off-screen the way a plain track scroll leaves it.
   *
   * @param {EventTarget|null} target - Where the arrow key was pressed.
   * @param {number} delta - -1 for previous, 1 for next.
   * @returns {boolean} Whether focus moved; false means the caller should scroll as usual.
   * @private
   */
  focusAdjacentItem(target, delta) {
    if (!this.options.focusFollowsArrows || !(target instanceof Element)) return false;

    const items = [...this.items];
    const current = items.findIndex((item) => item.contains(target));
    if (current === -1) return false;

    let next = current + delta;
    if (this.options.wrap) next = (next + items.length) % items.length;

    const focusable = items[next]?.querySelector(FOCUSABLE_SELECTOR);
    if (!focusable) return false;

    focusable.focus();
    return true;
  }

  /**
   * Get the index of the slide whose start best matches scroll position.
   * Nearest-by-start avoids falling back to 0 when no slide contains the viewport
   * center (wide slides / partial scroll) — that broke prev in snap mode.
   *
   * A track scrolled to its end counts as showing the last slide. A track can't
   * scroll past its own end, so when the last slide is narrower than the
   * viewport its start is never reached and the nearest start belongs to the
   * slide before it — prev would then skip a slide and a scroll listener would
   * never report the last one.
   *
   * @returns {number} Index of current slide (0-based)
   */
  getCurrentIndex() {
    if (!this.items.length) return 0;

    // Without overflow nothing scrolls, so "at the end" says nothing about
    // which slide is current.
    const { scrollLeft, clientWidth, scrollWidth } = this.container;
    const overflows = scrollWidth > clientWidth + SCROLL_END_TOLERANCE;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_END_TOLERANCE;
    if (overflows && atEnd) return this.items.length - 1;

    const origin = this.trackOrigin();
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.items.length; i++) {
      const dist = Math.abs(this.itemStart(this.items[i], origin) - scrollLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  /**
   * Register a scroll listener that fires when the slider finishes scrolling.
   * Debounced so the callback runs once per scroll, not per frame.
   *
   * @param {Function} callback - Called with current slide index when scroll completes
   * @param {number} callback.index - Current slide index
   * @param {Object} [options] - Optional listener options
   * @param {AbortSignal} [options.signal] - Signal used to remove listener and cancel pending debounce
   * @returns {void}
   */
  onScroll(callback, options) {
    let timeout;
    const clearPendingTimeout = () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    // Default to the slider's own controller so the scroll listener is removed
    // on destroy() even when the caller passes no signal — otherwise a consumer
    // that destroys + recreates on a persistent container (e.g. the UGC gallery)
    // stacks a scroll handler per reload. A caller-supplied signal still wins.
    const signal = options?.signal ?? this._abort?.signal;
    if (signal) {
      signal.addEventListener('abort', clearPendingTimeout, { once: true });
    }

    this.container.addEventListener('scroll', () => {
      clearPendingTimeout();
      timeout = setTimeout(() => {
        if (signal?.aborted) return;
        callback(this.getCurrentIndex());
      }, SCROLL_END_DEBOUNCE_MS);
    }, { passive: true, signal });
  }

  // ---------------------------------------------------------------------------
  // Autoplay (slide mode)
  // ---------------------------------------------------------------------------

  /**
   * Start automatic sliding at the configured interval. Always moves one
   * slide, start-aligned, and wraps to the first after the last regardless of
   * the wrap option.
   *
   * @returns {void}
   */
  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.scrollToItemStart((this.getCurrentIndex() + 1) % this.items.length);
    }, this.options.interval);
  }

  /**
   * Stop autoplay and clear the timer.
   *
   * @returns {void}
   */
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Restart the autoplay timer after a manual interaction so the next
   * automatic move isn't right on its heels. No-op unless autoplay is on.
   *
   * @returns {void}
   */
  resetAutoplay() {
    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  /**
   * Turn autoplay on or off at runtime, e.g. from a pause control (WCAG 2.2.2). While off,
   * arrow, button, and keyboard interaction no longer restarts it.
   *
   * @param {boolean} enabled
   * @returns {void}
   */
  setAutoplay(enabled) {
    this.options.autoplay = enabled;
    if (enabled && this.items.length > 1) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Re-query slide items from the DOM (call after dynamic content changes).
   * @returns {void}
   */
  refresh() {
    this.items = this.root.querySelectorAll('[data-slider-item]');
    this.updateControls();
  }

  /**
   * Scroll back to the first slide with no animation. Call after swapping in an
   * unrelated set of items (e.g. new search results replacing the previous
   * query's) so the fresh content doesn't inherit whatever scroll position the
   * old content happened to be left at.
   * @returns {void}
   */
  resetToStart() {
    if (!this.container) return;
    this.container.scrollLeft = 0;
  }

  /**
   * Show the arrows only while the track overflows, and disable the one that
   * can't move any further (never, with wrap).
   *
   * @returns {void}
   */
  updateControls() {
    if (!this.container) return;

    const hasOverflow = this.container.scrollWidth > this.container.clientWidth + SCROLL_END_TOLERANCE;

    if (this.prevBtn) {
      this.prevBtn.classList.toggle('hidden', !hasOverflow);
      this.prevBtn.classList.toggle('flex', hasOverflow);
    }
    if (this.nextBtn) {
      this.nextBtn.classList.toggle('hidden', !hasOverflow);
      this.nextBtn.classList.toggle('flex', hasOverflow);
    }

    if (!hasOverflow) return;

    if (this.options.wrap) {
      if (this.prevBtn) this.prevBtn.disabled = this.items.length <= 1;
      if (this.nextBtn) this.nextBtn.disabled = this.items.length <= 1;
      return;
    }

    const atStart = this.container.scrollLeft <= SCROLL_END_TOLERANCE;
    const atEnd = this.container.scrollLeft + this.container.clientWidth >= this.container.scrollWidth - SCROLL_END_TOLERANCE;

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  /**
   * Clean up all event listeners and timers.
   * Should be called before discarding the Slider instance.
   *
   * @returns {void}
   */
  destroy() {
    this.stopAutoplay();

    // Removes every listener bound with this._abort.signal (all persistent
    // init/bindSwipe/bindDrag handlers) in one shot.
    this._abort?.abort();
    this.cancelPendingSettle();
    this._abort = null;
    this._scrollHandler = null;
    this._swipeMouseUpHandler = null;
    this._resizeHandler = null;

    // Per-gesture drag listeners live on document (bound at mousedown, outside
    // the signal), so tear down any drag caught in progress explicitly.
    if (this._dragDocMove) {
      document.removeEventListener('mousemove', this._dragDocMove);
      document.removeEventListener('mouseup', this._dragDocEnd);
      if (this.container && this._touchEnd) {
        this.container.removeEventListener('touchend', this._touchEnd);
        this.container.removeEventListener('touchcancel', this._touchEnd);
        this.container.removeEventListener('touchmove', this._touchMoveTrack);
      }
      this._dragDocMove = null;
      this._dragDocEnd = null;
      this._touchEnd = null;
      this._touchMoveTrack = null;
    }
    this._dragActive = false;
    if (this.container && this._dragPrevScrollBehavior !== undefined) {
      this.container.style.scrollBehavior = this._dragPrevScrollBehavior;
      this._dragPrevScrollBehavior = undefined;
    }
  }
}
